import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus } from 'lucide-react';
import PhysioModal from '../components/PhysioModal';
import ZoomableImage from '../components/ZoomableImage';
import toast from 'react-hot-toast';
import ViewPhysioModal from '../components/ViewPhysioModal';

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- Icons ---
const TickIcon = () => <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const CrossIcon = () => <svg width="15" height="15" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const TC = ({ v }: { v: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    {v ? <TickIcon /> : <CrossIcon />}
  </div>
);

// --- Global Compact Table Styles ---
const globalCss = `
  .ctbl th, .ctbl td { padding: 8px 10px !important; font-size: 0.78rem !important; border: 1px solid #c5cbd3 !important; vertical-align: middle !important; white-space: nowrap; }
  .ctbl th { padding: 10px 10px !important; font-size: 0.78rem !important; text-transform: uppercase; letter-spacing: 0.04em; color: #1a3a5c; font-weight: 700; background: #86afd8 !important; text-align: center; }
  .ctbl thead { background: #86afd8 !important; }
  .ctbl { table-layout: fixed; width: 100% !important; }
  .ctbl td.wc { white-space: normal !important; }
`;

const PhysioTab = () => {
  const [physios, setPhysios] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPhysio, setEditingPhysio] = useState<any | null>(null);
  const [viewingStaff, setViewingStaff] = useState<any | null>(null);

  const handleEditClick = (physio: any) => {
    setEditingPhysio(physio);
    setIsModalOpen(true);
  };

  const handleViewClick = (physio: any) => {
    setViewingStaff(physio);
  };

  const handleOpenNewModal = () => {
    setEditingPhysio(null);
    setIsModalOpen(true);
  };

  const fetchPhysios = async () => {
    setIsLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_URL}/api/physiotherapists/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPhysios(res.data);
    } catch (err) {
      toast.error("Failed to load physiotherapists");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchPhysios(); }, []);

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to remove ${name}?`)) return;
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/api/physiotherapists/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Physiotherapist removed");
      fetchPhysios();
    } catch (err) {
      toast.error("Failed to remove physiotherapist");
    }
  };

  const getAgeAndSex = (dob: string, sex: string) => {
    let ageStr = '';
    if (dob) {
      const n = Math.abs(new Date(Date.now() - new Date(dob).getTime()).getUTCFullYear() - 1970);
      if (n > 0) ageStr = n.toString();
    }
    const s = (sex || '').toLowerCase();
    const sexStr = s.startsWith('f') ? 'Female' : s.startsWith('m') ? 'Male' : '';
    if (!ageStr && !sexStr) return 'N/A';
    return `${ageStr || '-'}/${sexStr || '-'}`;
  };

  const filteredPhysios = physios.filter(p => 
    p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.departments?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <style>{globalCss}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', position: 'relative', width: '300px' }}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text" placeholder="Search Name or Dept..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1', outline: 'none' }}
          />
        </div>
        <button onClick={handleOpenNewModal} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#0f172a', color: 'white', padding: '0.65rem 1.25rem', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                  <Plus size={16} /> Register Physio
        </button>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '9px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <table className="ctbl" style={{ width: '100%', borderCollapse: 'collapse', color: '#1d2631' }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Sl.No</th>
              <th style={{ width: '60px' }}>Photo</th>
              <th style={{ width: '120px', textAlign: 'left' }}>Name</th>
              <th style={{ width: '50px' }}>Age/Sex</th>
              <th style={{ width: '60px' }}>Exp</th>
              <th style={{ width: '150px' }}>Departments</th>
              <th style={{ width: '120px' }}>Languages</th>
              <th style={{ width: '40px' }}>Docs</th>
              <th style={{ width: '54px' }}>View</th>
              <th style={{ width: '54px' }}>Edit</th>
              <th style={{ width: '54px' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
                <tr><td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
            ) : filteredPhysios.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No physiotherapists found.</td></tr>
            ) : filteredPhysios.map((physio, index) => (
              <tr 
                key={physio.id} 
                style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: hoverRowId === physio.id ? '#f8fafc' : 'white', transition: 'background 0.1s' }}
                onMouseEnter={() => setHoverRowId(physio.id)}
                onMouseLeave={() => setHoverRowId(null)}
              >
                <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{index + 1}</td>
                
                <td style={{ textAlign: 'center' }}>
                  <ZoomableImage
                    src={physio.photo_url || PLACEHOLDER_IMG}
                    alt="Physio Photo"
                    style={{ width: '45px', height: '45px', borderRadius: '5px', objectFit: 'cover', border: '1px solid #e2e8f0', display: 'block', margin: '0 auto' }}
                    fallbackSrc={PLACEHOLDER_IMG}
                  />
                </td>
                
                <td className="wc" style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                  {physio.full_name}
                </td>
                
                <td style={{ textAlign: 'center' }}>{getAgeAndSex(physio.dob, physio.sex)}</td>
                <td style={{ textAlign: 'center' }}>{physio.experience_in_years ? `${physio.experience_in_years} Yrs` : '-'}</td>
                <td className="wc">{physio.departments || '-'}</td>
                <td className="wc">{physio.languages_spoken || '-'}</td>
                
                <td style={{ textAlign: 'center' }}>
                  <TC v={physio.documents?.length > 0} />
                </td>
                
                <td style={{ textAlign: 'center' }}>
                  <button onClick={() => handleViewClick(physio)} style={{ background: 'linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)' }}>View</button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button onClick={() => handleEditClick(physio)} style={{ background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' }}>Edit</button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button onClick={() => handleDelete(physio.id, physio.full_name)} style={{ background: 'linear-gradient(135deg,#f43f5e 0%,#e11d48 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(225,29,72,0.2)' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ViewPhysioModal
        isOpen={!!viewingStaff}
        onClose={() => setViewingStaff(null)}
        physio={viewingStaff}
      />      
      <PhysioModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSuccess={fetchPhysios} 
        editData={editingPhysio} 
      />
    </div>
  );
};

export default PhysioTab;